import React, { useEffect, useMemo, useState } from "react";
import { db, collection, getDocs } from "../boardData";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { WidgetShell, EmptyState } from "./common";

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ec4899", "#14b8a6", "#eab308"];

function toMillis(v) {
  if (!v) return 0;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

const PERIOD_DAYS = { "7d": 7, "30d": 30, "90d": 90 };

// Call-volume analytics aggregated client-side from the alarm history.
const RunStatsWidget = ({ config = {}, agency }) => {
  const [alarms, setAlarms] = useState([]);
  const days = PERIOD_DAYS[config.period] || 30;
  const chartType = config.chartType || "bar";

  useEffect(() => {
    if (!agency) return undefined;
    let cancelled = false;
    const load = async () => {
      const all = [];
      for (const ref of [collection(db, `${agency} Alarms`), collection(db, "Agencies", agency, "Alarms")]) {
        try {
          const snap = await getDocs(ref);
          snap.forEach((d) => all.push(d.data()));
        } catch (e) { /* ignore missing path */ }
      }
      if (!cancelled) setAlarms(all);
    };
    load();
    const t = setInterval(load, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [agency]);

  const { byType, byDay, total } = useMemo(() => {
    const since = Date.now() - days * 24 * 60 * 60 * 1000;
    const recent = alarms.filter((a) => {
      const ts = toMillis(a.TimeStamp || a.Eventtime || a.EventTime || a.postTime);
      return !ts || ts >= since;
    });
    const typeMap = {};
    const dayMap = {};
    recent.forEach((a) => {
      const type = a.CallType || a.callType || a.callCategory || "Other";
      typeMap[type] = (typeMap[type] || 0) + 1;
      const ts = toMillis(a.TimeStamp || a.Eventtime || a.postTime);
      if (ts) {
        const key = new Date(ts).toLocaleDateString([], { month: "short", day: "numeric" });
        dayMap[key] = (dayMap[key] || 0) + 1;
      }
    });
    const byTypeArr = Object.entries(typeMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    const byDayArr = Object.entries(dayMap).map(([name, value]) => ({ name, value }));
    return { byType: byTypeArr, byDay: byDayArr, total: recent.length };
  }, [alarms, days]);

  if (total === 0) {
    return (
      <WidgetShell title="Call Volume">
        <EmptyState icon="📊" label="No calls in this period." />
      </WidgetShell>
    );
  }

  return (
    <WidgetShell title={`Call Volume · ${total} (${config.period || "30d"})`}>
      <div style={{ width: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart data={byDay} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
              <XAxis dataKey="name" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
              <YAxis tick={{ fill: "#cbd5e1", fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={false} />
            </LineChart>
          ) : chartType === "pie" ? (
            <PieChart>
              <Pie data={byType} dataKey="value" nameKey="name" outerRadius="80%" label>
                {byType.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          ) : (
            <BarChart data={byType} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
              <XAxis dataKey="name" tick={{ fill: "#cbd5e1", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fill: "#cbd5e1", fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value">
                {byType.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </WidgetShell>
  );
};

export default RunStatsWidget;
